import { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Calendar,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  LogIn,
  LogOut,
  Palmtree,
  RefreshCw,
  Shield,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { AuthContext } from "../Context/AuthContext";
import {
  buildSelfEmployeeFromProfile,
  formatEmployeeLabel,
  resolveEmployeeIdFromProfile,
} from "../../components/common/employeeUtils";
import DatePickerField from "../../components/common/DatePickerField";
import IndianMonthCalendar, { CalendarLegend } from "../../components/attendance/IndianMonthCalendar";
import { StatusBadge } from "../../components/pages/Attendance/weekly/attendanceUi";
import { normalizeAttendanceStatus } from "./attendanceApi";
import { formatMonthYearIndian } from "./indianCalendarUtils";
import { EMPLOYEE_WORKSPACE_ROUTES } from "../../config/employeeWorkspaceNav";
import {
  currentMonthKey,
  dayOfMonth,
  formatDisplayDate,
  pickDefaultDay,
  shiftMonth,
  shortWeekday,
  todayIso,
} from "./employee/myAttendanceDateUtils";
import { resolvePeriod, useMyAttendance } from "./employee/useMyAttendance";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";
import "../../styles/IndianCalendar.css";
import "./MyAttendancePage.css";

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
};

function employeeInitials(profile) {
  const emp = profile?.employee;
  const first = emp?.first_name?.[0] || "";
  const last = emp?.last_name?.[0] || "";
  return (first + last).toUpperCase() || "ME";
}

function monthBoundsFromKey(monthKey) {
  const range = resolvePeriod("month", monthKey, "", "", (k) => formatMonthYearIndian(k).label);
  return range ? { from: range.from_date, to: range.to_date } : { from: "", to: "" };
}

export default function MyAttendancePage() {
  const { user } = useContext(AuthContext);
  const selfEmployee = buildSelfEmployeeFromProfile(user);
  const employeeId = resolveEmployeeIdFromProfile(user);

  const [viewMode, setViewMode] = useState("month");
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [customFrom, setCustomFrom] = useState(() => monthBoundsFromKey(currentMonthKey()).from);
  const [customTo, setCustomTo] = useState(() => monthBoundsFromKey(currentMonthKey()).to);
  const [selectedDate, setSelectedDate] = useState(todayIso);

  const period = useMemo(
    () =>
      resolvePeriod(viewMode, monthKey, customFrom, customTo, (k) => formatMonthYearIndian(k).label),
    [viewMode, monthKey, customFrom, customTo]
  );

  const calendarMonthKey = viewMode === "month" ? monthKey : selectedDate.slice(0, 7);

  const {
    rawRows,
    sortedRows,
    byDate,
    holidayByDate,
    holidayDayMap,
    dayStatusMap,
    monthSummary,
    totalHours,
    attendanceRate,
    calendar,
    loading,
    error,
    reload,
  } = useMyAttendance({ employeeId, period, calendarMonthKey });

  const selectedRecord = byDate.get(selectedDate) || null;
  const isTodaySelected = selectedDate === todayIso();
  const highlightDay = selectedDate.startsWith(calendarMonthKey)
    ? Number(dayOfMonth(selectedDate))
    : null;

  const goToToday = () => {
    const today = todayIso();
    setSelectedDate(today);
    setMonthKey(today.slice(0, 7));
    if (viewMode === "custom") {
      const b = monthBoundsFromKey(today.slice(0, 7));
      setCustomFrom(b.from);
      setCustomTo(b.to);
    }
  };

  if (!employeeId) {
    return (
      <div className="mat-root min-h-screen bg-[#eef2f7] p-6">
        <div className="mat-page mat-panel p-8 text-center text-slate-600 text-sm">
          Unable to load your employee profile. Sign in with your employee account or contact HR.
        </div>
      </div>
    );
  }

  const displayName = formatEmployeeLabel(selfEmployee) || "You";
  const heroStats = [
    { label: "Present", value: loading ? "—" : monthSummary?.P ?? 0 },
    { label: "Absent", value: loading ? "—" : monthSummary?.A ?? 0 },
    { label: "Hours", value: loading ? "—" : `${totalHours.toFixed(1)}h` },
  ];

  return (
    <div className="mat-root min-h-screen bg-[#eef2f7] py-4 sm:py-5 md:py-6 px-4 sm:px-5 md:px-6">
      <div className="mat-page">
        <motion.header {...fadeUp} className="mat-hero">
          <div className="mat-hero-row">
            <div className="mat-hero-main">
              <div className="mat-hero-icon" aria-hidden>
                <CalendarDays size={22} strokeWidth={1.75} />
              </div>
              <div className="mat-hero-text">
                <div className="mat-hero-badge">
                  <Sparkles size={10} />
                  Employee · Private
                </div>
                <h1 className="mat-hero-title">My Attendance</h1>
                <p className="mat-hero-sub">
                  Welcome back, {displayName.split(" (")[0]}. Review your check-ins, hours, and
                  monthly summary — only your data is shown here.
                </p>
                <div className="mat-hero-stats">
                  {heroStats.map((s) => (
                    <span key={s.label} className="mat-hero-stat">
                      <span className="mat-hero-stat-val">{s.value}</span>
                      <span className="mat-hero-stat-lbl">{s.label}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mat-hero-actions">
              <button type="button" className="mat-btn mat-btn--solid" onClick={goToToday}>
                <Clock size={15} />
                Today
              </button>
              <button
                type="button"
                className="mat-btn mat-btn--ghost"
                onClick={reload}
                disabled={loading}
              >
                <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>
        </motion.header>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mat-alert mat-alert--error"
            role="alert"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.04 }}
          className="mat-toolbar mat-panel mb-4"
        >
          <div className="mat-panel-body">
            <div className="mat-toolbar-row">
              <div className="mat-self-chip">
                <span className="mat-self-avatar">{employeeInitials(user)}</span>
                <div>
                  <span className="mat-self-name">{displayName}</span>
                  <span className="mat-self-meta">
                    <Shield size={11} />
                    Read-only · your records only
                  </span>
                </div>
              </div>
              <div className="mat-seg" role="tablist" aria-label="View mode">
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === "month"}
                  className={`mat-seg-btn${viewMode === "month" ? " is-active" : ""}`}
                  onClick={() => setViewMode("month")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === "custom"}
                  className={`mat-seg-btn${viewMode === "custom" ? " is-active" : ""}`}
                  onClick={() => setViewMode("custom")}
                >
                  Date range
                </button>
              </div>
            </div>

            {viewMode === "month" ? (
              <div className="mat-month-nav">
                <button
                  type="button"
                  className="mat-nav-btn"
                  onClick={() => {
                    const next = shiftMonth(monthKey, -1);
                    setMonthKey(next);
                    setSelectedDate(pickDefaultDay(next));
                  }}
                  aria-label="Previous month"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="mat-month-label">
                  <Calendar size={16} className="text-teal-600" />
                  {formatMonthYearIndian(monthKey).label}
                </div>
                <button
                  type="button"
                  className="mat-nav-btn"
                  onClick={() => {
                    const next = shiftMonth(monthKey, 1);
                    setMonthKey(next);
                    setSelectedDate(pickDefaultDay(next));
                  }}
                  aria-label="Next month"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            ) : (
              <div className="mat-filters">
                <DatePickerField
                  label="From"
                  value={customFrom}
                  onChange={setCustomFrom}
                  max={customTo || undefined}
                />
                <DatePickerField
                  label="To"
                  value={customTo}
                  onChange={setCustomTo}
                  min={customFrom || undefined}
                />
              </div>
            )}
          </div>
        </motion.div>

        {viewMode === "month" && monthSummary && (
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.06 }}
            className="mat-stats mb-4"
          >
            <div className="mat-stat mat-stat--present">
              <p className="mat-stat-label">Present</p>
              <p className="mat-stat-value">{loading ? "—" : monthSummary.P}</p>
            </div>
            <div className="mat-stat mat-stat--absent">
              <p className="mat-stat-label">Absent</p>
              <p className="mat-stat-value">{loading ? "—" : monthSummary.A}</p>
            </div>
            <div className="mat-stat mat-stat--leave">
              <p className="mat-stat-label">Leave / Half day</p>
              <p className="mat-stat-value">{loading ? "—" : monthSummary.L + monthSummary.HD}</p>
            </div>
            <div className="mat-stat mat-stat--hours">
              <p className="mat-stat-label">Total hours</p>
              <p className="mat-stat-value">{loading ? "—" : `${totalHours.toFixed(1)}h`}</p>
            </div>
          </motion.div>
        )}

        {viewMode === "month" && !loading && monthSummary && (
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.07 }}
            className="mat-rate-card mat-panel mb-4"
          >
            <div className="mat-panel-body mat-rate-inner">
              <div className="mat-rate-icon">
                <TrendingUp size={20} />
              </div>
              <div className="mat-rate-text">
                <p className="mat-rate-title">Attendance score this month</p>
                <p className="mat-rate-sub">
                  Based on present and half-days among marked working days
                </p>
              </div>
              <div className="mat-rate-value">{attendanceRate}%</div>
            </div>
            <div className="mat-rate-bar" aria-hidden>
              <div className="mat-rate-fill" style={{ width: `${attendanceRate}%` }} />
            </div>
          </motion.div>
        )}

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.08 }}
          className="mat-grid"
        >
          <div className="mat-panel">
            <div className="mat-panel-head">
              <div>
                <h2 className="mat-panel-title">Calendar</h2>
                <p className="mat-panel-sub">Tap any day to inspect punches</p>
              </div>
            </div>
            <div className="mat-panel-body !p-0">
              {loading ? (
                <div className="p-8">
                  <BrixigoPageLoader compact message="Loading calendar…" fullPage={false} />
                </div>
              ) : (
                <IndianMonthCalendar
                  monthKey={calendarMonthKey}
                  cells={calendar.cells}
                  weekdayHeaders={calendar.weekdayHeaders}
                  holidayMap={holidayDayMap}
                  dayStatusMap={dayStatusMap}
                  highlightDay={highlightDay}
                  onDayClick={(day) => {
                    const iso = `${calendarMonthKey}-${String(day).padStart(2, "0")}`;
                    setSelectedDate(iso);
                  }}
                  readOnly
                  // Legend is rendered separately below as `CalendarLegend compact`.
                  // Keeping this off prevents duplicate "Present/Absent/Leave..." rows.
                  showLegend={false}
                />
              )}
              <div className="px-4 pb-4">
                <CalendarLegend compact />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className={`mat-day-card${isTodaySelected ? " is-today" : ""}`}>
              <div className="mat-day-head">
                <div>
                  <p className="mat-day-eyebrow">{isTodaySelected ? "Today" : "Selected day"}</p>
                  <h2 className="mat-day-title">{formatDisplayDate(selectedDate)}</h2>
                </div>
                {selectedRecord?.status ? (
                  <StatusBadge status={selectedRecord.status} />
                ) : holidayByDate[selectedDate] ? (
                  <StatusBadge status="holiday" />
                ) : null}
              </div>

              {holidayByDate[selectedDate] && (
                <p className="mat-holiday-note">Holiday · {holidayByDate[selectedDate].name}</p>
              )}

              <div className="mat-punch-cards">
                <div className="mat-punch-card">
                  <div className="mat-punch-icon mat-punch-icon--in">
                    <LogIn size={18} />
                  </div>
                  <div>
                    <p className="mat-punch-label">Check in</p>
                    <p className="mat-punch-value">
                      {selectedRecord?.checkIn || (loading ? "…" : "—")}
                    </p>
                  </div>
                </div>
                <div className="mat-punch-card">
                  <div className="mat-punch-icon mat-punch-icon--out">
                    <LogOut size={18} />
                  </div>
                  <div>
                    <p className="mat-punch-label">Check out</p>
                    <p className="mat-punch-value">
                      {selectedRecord?.checkOut || (loading ? "…" : "—")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mat-hours-row">
                <Clock size={15} className="text-teal-600" />
                <span>
                  Hours worked:{" "}
                  <strong>{selectedRecord ? `${selectedRecord.hoursWorked || 0}h` : "—"}</strong>
                </span>
              </div>

              {!loading && !selectedRecord && !holidayByDate[selectedDate] && (
                <p className="mat-day-empty">No attendance marked for this day yet.</p>
              )}

              <Link to={EMPLOYEE_WORKSPACE_ROUTES.leave} className="mat-quick-link">
                <Palmtree size={14} />
                Apply for leave
              </Link>
            </div>

            <div className="mat-panel">
              <div className="mat-panel-head">
                <div>
                  <h2 className="mat-panel-title">Activity log</h2>
                  <p className="mat-panel-sub">{period?.label || "—"}</p>
                </div>
              </div>
              <div className="mat-table-wrap">
                {loading ? (
                  <div className="p-6">
                    <BrixigoPageLoader compact fullPage={false} />
                  </div>
                ) : sortedRows.length === 0 ? (
                  <p className="mat-empty">No records in this period.</p>
                ) : (
                  <table className="mat-table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>In</th>
                        <th>Out</th>
                        <th>Hrs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row) => (
                        <tr
                          key={row.date}
                          className={`${row.date === selectedDate ? "is-selected" : ""}${
                            row.date === todayIso() ? " is-today" : ""
                          }`}
                          onClick={() => setSelectedDate(row.date)}
                        >
                          <td className="text-slate-500">{shortWeekday(row.date)}</td>
                          <td className="tabular-nums">{row.date}</td>
                          <td>
                            {row.status ? (
                              <StatusBadge status={row.status} />
                            ) : (
                              normalizeAttendanceStatus(
                                rawRows.find(
                                  (r) => String(r.work_date || "").slice(0, 10) === row.date
                                )?.status
                              ) || "—"
                            )}
                          </td>
                          <td className="tabular-nums">{row.checkIn || "—"}</td>
                          <td className="tabular-nums">{row.checkOut || "—"}</td>
                          <td className="tabular-nums">{row.hoursWorked || 0}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
